import { TemplateResult, html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksElement } from '../BlocksElement';
import type { CardVariant } from './card.model';
import styles from './card.styles';

/**
 * @slot header - The component header content
 * @slot default - The component default content
 * @slot footer - The component footer content
 * @csspart base - The component's base wrapper.
 * @csspart header-wrapper - The component's header wrapper.
 * @csspart part-header - The component's header slot.
 * @csspart body - The component's body content.
 * @csspart part-default - The component's default slot.
 * @csspart footer-wrapper - The component's footer wrapper.
 * @csspart part-footer - The component's footer slot.
 */
export class BlocksCard extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Option to remove padding
   */
  @property({ type: Boolean })
  removePadding: boolean | undefined;

  /**
   * Href attribute
   */
  @property({ type: String })
  href: string | undefined;

  /**
   * Style variants
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**bare** renders a card without a border or drop shadow</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  variant: CardVariant;

  /**
   * Allow a card to be auto height
   */
  @property({ type: Boolean })
  isAutoHeight: boolean | undefined;

  /**
   * Set card body max-height for cards with long body copy that need the ability to scroll
   */
  @property({ type: String })
  bodyMaxHeight: string | undefined;

  protected slots = new SlotController(this, 'header', null, 'footer');

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-card', {
      'cat-c-card--bare': this.variant === 'bare',
      'cat-c-card--remove-padding': this.removePadding,
      'cat-c-card--has-header-slot': this.slots.hasSlotted('header'),
      'cat-c-card--has-footer-slot': this.slots.hasSlotted('footer')
    });

    if (this.href) {
      return html`
        <a part="base" href=${this.href} class="${componentClassNames}" style="${ifDefined(this.isAutoHeight ? 'height: auto;' : undefined)}">
          <div part="header-wrapper" class="cat-c-card__header"><slot part="part-header" name="header"></slot></div>

          <div
            part="body"
            class="cat-c-card__body"
            style="${ifDefined(
              this.bodyMaxHeight ? '--cat-text-passage-overflow: auto; --cat-text-passage-max-height: ' + this.bodyMaxHeight + ';' : undefined
            )}"
          >
            <slot part="part-default"></slot>
          </div>

          <div part="footer-wrapper" class="cat-c-card__footer"><slot part="part-footer" name="footer"></slot></div>
        </a>
      `;
    } else {
      return html`
        <div part="base" class="${componentClassNames}" style="${ifDefined(this.isAutoHeight ? 'height: auto;' : undefined)}">
          <div part="header-wrapper" class="cat-c-card__header"><slot part="part-header" name="header"></slot></div>
          <div
            part="body"
            class="cat-c-card__body"
            style="${ifDefined(
              this.bodyMaxHeight ? '--cat-text-passage-overflow: auto; --cat-text-passage-max-height: ' + this.bodyMaxHeight + ';' : undefined
            )}"
          >
            <slot part="part-default"></slot>
          </div>

          <div part="footer-wrapper" class="cat-c-card__footer"><slot part="part-footer" name="footer"></slot></div>
        </div>
      `;
    }
  }
}

if (customElements.get('cat-card') === undefined) {
  customElements.define('cat-card', BlocksCard);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-card': BlocksCard;
  }
}
